import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, Check } from "lucide-react";

// Dropdown custom (no select nativo). Animación sutil, click-outside, Escape, teclado.
export default function CustomSelect({
    value,
    onChange,
    options,
    placeholder = "Seleccioná",
    label,
    icon: Icon,
    testId,
}) {
    const [open, setOpen] = useState(false);
    const [active, setActive] = useState(-1);
    const ref = useRef(null);
    const listRef = useRef(null);

    useEffect(() => {
        const onDoc = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener("mousedown", onDoc);
        return () => document.removeEventListener("mousedown", onDoc);
    }, []);

    useEffect(() => {
        if (open) setActive(options.indexOf(value));
    }, [open, options, value]);

    const choose = (opt) => {
        onChange(opt);
        setOpen(false);
    };

    const onKeyDown = (e) => {
        if (e.key === "Escape") return setOpen(false);
        if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            if (!open) return setOpen(true);
            if (active >= 0 && active < options.length) choose(options[active]);
            return;
        }
        if (e.key === "ArrowDown") {
            e.preventDefault();
            if (!open) return setOpen(true);
            setActive((a) => Math.min(a + 1, options.length - 1));
        }
        if (e.key === "ArrowUp") {
            e.preventDefault();
            setActive((a) => Math.max(a - 1, 0));
        }
    };

    return (
        <div ref={ref} className="relative w-full">
            {label && (
                <span className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-brand">
                    {Icon && <Icon className="h-3.5 w-3.5" />} {label}
                </span>
            )}
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                onKeyDown={onKeyDown}
                data-testid={testId}
                aria-haspopup="listbox"
                aria-expanded={open}
                className="mt-1 flex w-full items-center justify-between gap-2 bg-transparent text-left text-sm font-medium outline-none"
            >
                <span className={value ? "text-ink" : "text-ink/50"}>
                    {value || placeholder}
                </span>
                <ChevronDown
                    className={`h-4 w-4 shrink-0 text-ink/40 transition-transform duration-200 ${
                        open ? "rotate-180" : ""
                    }`}
                />
            </button>

            <AnimatePresence>
                {open && (
                    <motion.ul
                        ref={listRef}
                        role="listbox"
                        initial={{ opacity: 0, y: -6, scale: 0.98 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: -6, scale: 0.98 }}
                        transition={{ duration: 0.18, ease: [0.16, 1, 0.3, 1] }}
                        data-testid={testId ? `${testId}-menu` : undefined}
                        className="absolute left-0 top-full z-[60] mt-3 max-h-72 w-[min(20rem,calc(100vw-2.5rem))] overflow-y-auto rounded-2xl border border-black/5 bg-white p-1.5 shadow-2xl shadow-black/15 ring-1 ring-black/5"
                    >
                        {options.length === 0 && (
                            <li className="px-3 py-2.5 text-sm text-ink/50">
                                Sin opciones disponibles
                            </li>
                        )}
                        {options.map((opt, i) => {
                            const selected = opt === value;
                            const isActive = i === active;
                            return (
                                <li key={opt}>
                                    <button
                                        type="button"
                                        role="option"
                                        aria-selected={selected}
                                        onMouseEnter={() => setActive(i)}
                                        onClick={() => choose(opt)}
                                        data-testid={
                                            testId ? `${testId}-opt-${opt}` : undefined
                                        }
                                        className={`flex w-full items-center justify-between gap-2 rounded-xl px-3 py-2.5 text-left text-sm transition-colors duration-150 ${
                                            isActive
                                                ? "bg-brand-light text-brand"
                                                : "text-ink hover:bg-brand-light"
                                        }`}
                                    >
                                        <span className="font-medium">{opt}</span>
                                        {selected && (
                                            <Check className="h-4 w-4 text-brand" />
                                        )}
                                    </button>
                                </li>
                            );
                        })}
                    </motion.ul>
                )}
            </AnimatePresence>
        </div>
    );
}
